import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Homepage
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2 text-[#0047AB]">Cookie Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: January 28, 2025</p>
        
        <div className="prose dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">What are Cookies?</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Cookies are small text files that are stored on your computer or mobile device when 
              you visit our website. They help us remember your preferences and provide you with 
              a better user experience.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">What Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg border border-green-200 dark:border-green-900">
                <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-2">Necessary Cookies (Always Active)</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  These cookies are required for the basic functions of the website, such as secure 
                  login or storing your consent preferences. These cookies do not store any 
                  personally identifiable information.
                </p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mt-2 space-y-1">
                  <li><strong>auth_token</strong> – Authentication and session management</li>
                  <li><strong>cookie_consent</strong> – Storing your cookie settings</li>
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
                <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-2">Functional Cookies</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Functional cookies help perform certain functions such as sharing content on 
                  social media platforms, collecting feedback, and other third-party features.
                </p>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mt-2 space-y-1">
                  <li><strong>theme</strong> – Storing your preferred design setting (light/dark)</li>
                  <li><strong>language</strong> – Storing your language preference</li>
                </ul>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-950/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-900">
                <h3 className="text-lg font-semibold text-yellow-700 dark:text-yellow-400 mb-2">Analytical Cookies</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Analytical cookies are used to understand how visitors interact with the website. 
                  These cookies help provide information on metrics such as the number of visitors, 
                  bounce rate, traffic source, etc.
                </p>
                <p className="text-slate-500 dark:text-slate-500 italic mt-2">
                  Currently, no analytical cookies are used.
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-950/20 p-4 rounded-lg border border-purple-200 dark:border-purple-900">
                <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-400 mb-2">Advertising Cookies</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Advertising cookies are used to show visitors personalized advertising based on 
                  previously visited pages and to analyze the effectiveness of advertising campaigns.
                </p>
                <p className="text-slate-500 dark:text-slate-500 italic mt-2">
                  Currently, no advertising cookies are used.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">How to Manage Cookies</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              You can manage your cookie settings at any time through your browser. Most browsers 
              allow you to:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-slate-400 mt-2 space-y-2">
              <li>View and delete all cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block third-party cookies</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when you close the browser</li>
            </ul>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              Please note that blocking all cookies may affect the functionality of this and many 
              other websites you visit.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">More Information</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              For more information about how we process your data, please see our{' '}
              <a href="/privacy-policy" className="text-[#0047AB] hover:underline">Privacy Policy</a>.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              If you have any questions about our cookie policy, please contact us at:{' '}
              <a href="mailto:info@margolytics.com" className="text-[#0047AB] hover:underline">info@margolytics.com</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicy;
